import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertCircle, Loader2 } from "lucide-react";
import { useRequestShipmentCancellation } from "@/api/shipments";
import { useQueryClient } from "@tanstack/react-query";

const CANCELLATION_REASONS = [
  { value: "customer_request", label: "Customer requested cancellation" },
  { value: "duplicate_order", label: "Duplicate order" },
  { value: "pricing_error", label: "Pricing error" },
  { value: "address_issue", label: "Address issue - cannot deliver" },
  { value: "out_of_stock", label: "Item out of stock" },
  { value: "shipment_canceled", label: "Shipment canceled by operator" },
  { value: "other", label: "Other (custom)" },
];

interface CancelShipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: {
    id: string;
    shipmentNumber?: number;
    sellAmountCents?: number | null;
    status?: string;
  } | null;
  onSuccess?: () => void;
}

export function CancelShipmentDialog({
  open,
  onOpenChange,
  shipment,
  onSuccess,
}: CancelShipmentDialogProps) {
  const queryClient = useQueryClient();
  const [reasonValue, setReasonValue] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");

  const cancelMutation = useRequestShipmentCancellation({
    onSuccess: (data) => {
      if (data.immediate) {
        toast.success("Shipment Cancelled", {
          description: data.tasksCancelled
            ? `${data.tasksCancelled} tasks cancelled`
            : data.message,
        });
      } else {
        toast.success("Cancellation Requested", {
          description:
            "Review in Pending Cancellations to process refund and finalize.",
        });
      }

      void queryClient.invalidateQueries({ queryKey: ["shipments"] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({
        queryKey: ["pending-cancellations"],
      });

      handleClose();
      onSuccess?.();
    },
    onError: (error) => {
      toast.error("Failed to request cancellation", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    setReasonValue("");
    setCustomReason("");
    setNotes("");
  };

  const handleCancel = () => {
    if (!shipment) return;

    const reason =
      reasonValue === "other"
        ? customReason.trim()
        : CANCELLATION_REASONS.find((r) => r.value === reasonValue)?.label ||
          reasonValue;

    if (!reason) {
      toast.error("Reason required", {
        description: "Please select or enter a cancellation reason.",
      });
      return;
    }

    const fullReason = notes.trim() ? `${reason}. Notes: ${notes}` : reason;

    cancelMutation.mutate({
      shipmentId: shipment.id,
      reason: fullReason,
    });
  };

  const isPendingPayment = shipment?.status === "PENDING_PAYMENT";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Shipment</DialogTitle>
          <DialogDescription>
            Cancel SHP-{shipment?.shipmentNumber ?? ""}
            {shipment?.sellAmountCents != null && (
              <> (${(shipment.sellAmountCents / 100).toFixed(2)})</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {isPendingPayment
                ? "This will cancel all tasks for this shipment."
                : "This will cancel all tasks. You can process refunds from Pending Cancellations."}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Cancellation Reason</Label>
            <Select
              value={reasonValue}
              onValueChange={setReasonValue}
              disabled={cancelMutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reasonValue === "other" && (
            <div className="space-y-2">
              <Label htmlFor="custom-reason">Custom Reason</Label>
              <Textarea
                id="custom-reason"
                placeholder="Enter the reason for cancellation..."
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                rows={2}
                disabled={cancelMutation.isPending}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="cancel-notes">Notes (optional)</Label>
            <Textarea
              id="cancel-notes"
              placeholder="Additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              disabled={cancelMutation.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Keep Shipment
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={
              cancelMutation.isPending ||
              !reasonValue ||
              (reasonValue === "other" && !customReason.trim())
            }
          >
            {cancelMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Cancel Shipment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
